import { Grid, Stack, styled, TextField, Typography } from '@mui/material';
import { useForm } from 'react-hook-form';

import {
  DocumentTemplateField,
  DocumentTemplateFields,
} from '../../services/types/letters-types';

interface LetterFormData {
  [key: string]: string;
}

const StyledTextField = styled(TextField)({
  '& .MuiOutlinedInput-root': {
    backgroundColor: 'transparent',
    borderRadius: '4px',
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(0, 0, 0, 0.12)',
      borderRadius: '4px',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(0, 0, 0, 0.24)',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: 'primary.main',
      borderWidth: 1,
    },
  },
});

/**
 * Shared presentational components used by both Matter-centric `CreateLetter` dialog
 * and Intake-centric `SendLoeDialog`.
 *
 * 1. `LetterForm`
 *    Renders a grid of template fields as controlled inputs and posts values via
 *    react-hook-form.  Required fields are indicated using `requiredMappings` so
 *    the same component works for templates that need signer info (LOE/contract)
 *    and those that do not.
 *
 * 2. `LetterFormContent`
 *    Thin wrapper that shows a loading / empty-state and, once the template
 *    fields are fetched, renders `LetterForm`.
 *
 * The components are *pure* UI—no network calls—so they can be reused anywhere
 * a "fill in merge-fields" UI is required.
 */
export const LetterForm = ({
  fields,
  onSubmit,
  requiredMappings,
  initialValues,
}: {
  fields: DocumentTemplateField[];
  onSubmit: (data: LetterFormData) => void | Promise<void>;
  requiredMappings: string[];
  initialValues?: Record<string, string>;
}) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LetterFormData>();

  return (
    <form id="letter-form" onSubmit={handleSubmit(onSubmit)}>
      <Grid container spacing={5}>
        {fields.map((field) => (
          <Grid item xs={6} key={field.id}>
            <Stack spacing={1}>
              <Typography
                variant="body3"
                sx={{
                  textTransform: 'uppercase',
                  lineHeight: 1.25,
                  color: (theme) => theme.typography.subtitle1.color,
                }}
              >
                {field.display_name}
              </Typography>
              <StyledTextField
                {...register(field.external_reference, {
                  required: requiredMappings.includes(field.mapping)
                    ? 'This field is required'
                    : false,
                })}
                defaultValue={
                  initialValues?.[field.external_reference] ??
                  field.default_value
                }
                fullWidth
                multiline
                size="small"
                error={!!errors[field.external_reference]}
                helperText={errors[field.external_reference]?.message}
              />
            </Stack>
          </Grid>
        ))}
      </Grid>
    </form>
  );
};

export const LetterFormContent = ({
  isLoading,
  templateFields,
  onSubmit,
  requiredMappings,
  initialValues,
}: {
  isLoading: boolean;
  templateFields?: DocumentTemplateFields;
  onSubmit: (data: LetterFormData) => void | Promise<void>;
  requiredMappings: string[];
  initialValues?: Record<string, string>;
}) => {
  if (isLoading) {
    return <Typography>Loading template fields...</Typography>;
  }

  if (!templateFields?.fields) {
    return <Typography>No fields found for this template.</Typography>;
  }

  return (
    <LetterForm
      fields={templateFields.fields}
      onSubmit={onSubmit}
      requiredMappings={requiredMappings}
      initialValues={initialValues}
    />
  );
};
